import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CardActions,
  CircularProgress,
  Divider,
} from "@mui/material";
import { ExpenseDashBoardStore } from "../../../../../store/main/ExpenseDashBoardStore";
import Backdrop from "@mui/material/Backdrop";
import ImagePreviewModal from "./ImagePreviewModal";

type UploadedFile = {
  id: number;
  expense_id: number;
  file_uuid: string;
  uploaded_by: number;
  expense_date: string;
  status: string;
};

type ManagerExpenseApprovalRows = {
  empRole: string;
  uploadedFiles: UploadedFile[];
  fullName: string;
  id: number;
  date: string;
  station: string | null;
  locality: string | null;
  totalDistanceKm: string;
  dailyAllowance: string;
  travelAllowance: string;
  fixAllowance: string | null;
  foodAllowance: string | null;
  remark: string | null;
  status: string;
  expectedAmount: string | null;
  attendanceType: string;
  approvedStatus: string;
  total: string;
  totalFinalAmount: string;
  workingBrick: string | null;
  userId: number;
  systemRemarks: string | null;
  miscellaneousExpenseTypeId: number | null;
  miscellaneousExpenseType: string | null;
  miscellaneousExpenseAmount: number | null;
  miscellaneousExpenseAmountError?: string;
  approvedBy?: number | null;
  approverName?: string | null;
};

type ExistingFile = {
  uuid: string;
  url: string;
};

type FileReadManagerModalProps = {
  open: boolean;
  onClose: () => void;
  rowId: number | null;
  uuids: string[];
  onSuccess: () => void;
};

const FileReadManagerModal = ({
  open,
  onClose,
  rowId,
  uuids,
  onSuccess,
}: FileReadManagerModalProps) => {
  const { addReadFileExpense } = ExpenseDashBoardStore();

  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    if (!open || !rowId || !uuids || uuids.length === 0) {
      setExistingFiles([]);
      return;
    }

    let cancelled = false;

    const fetchFiles = async () => {
      try {
        setLoadingModal(true);
        const res = await addReadFileExpense({ uuids });

        if (!cancelled) {
          setExistingFiles(res?.data?.urls ?? []);
        }
      } catch (err) {
        if (!cancelled) setExistingFiles([]);
        console.error(err);
      } finally {
        if (!cancelled) setLoadingModal(false);
      }
    };

    fetchFiles();

    return () => {
      cancelled = true;
    };
  }, [open, rowId, uuids]);

  const handleExistingFileClick = (file: ExistingFile) => {
    const cleanUrl = file.url.split("?")[0];
    const isPdf = cleanUrl.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      window.open(file.url, "_blank");
    } else {
      setPreviewImage(file.url);
    }
  };

  const handleNewFileClick = (file: File) => {
    const url = URL.createObjectURL(file);
    const isPdf = file.type.includes("pdf");

    if (isPdf) {
      window.open(url, "_blank");
    } else {
      setPreviewImage(url);
    }
  };

  const handleClose = () => {
    if (previewImage?.startsWith("blob:")) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
    setNewFiles([]);
    setExistingFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setPreviewImage(null);
      setNewFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0,0,0,0.5)",
              pointerEvents: "none",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "70%",
            height: "80%",
            bgcolor: "#ffffff",
            overflow: "hidden",
            p: 3,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            opacity: loadingModal ? 0.4 : 1,
            pointerEvents: loadingModal ? "none" : "auto",
            border: "2px solid #030000ff",
            boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {loadingModal && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(2px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
              }}
            >
              <CircularProgress size={50} />
            </Box>
          )}

          <Typography variant="h6" textAlign="center" mb={1}>
            Uploaded Files
          </Typography>
          <Divider
            sx={{
              borderBottomWidth: 2,
              borderColor: "#cccccc",
              mb: 1,
            }}
          />

          <Box sx={{ flex: 1, overflowY: "auto", mt: 1 }}>
            {existingFiles.length === 0 && newFiles.length === 0 ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  color="textSecondary"
                  textAlign="center"
                >
                  No files uploaded
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {existingFiles.map((file, index) => {
                  const cleanUrl = file.url.split("?")[0];
                  const isImage = /\.(jpeg|jpg|png)$/i.test(cleanUrl);

                  return (
                    <Grid item xs={6} sm={3} md={2} key={`existing-${index}`}>
                      <Card
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          boxShadow: 3,
                          height: 140,
                          border: "1px solid #b59f00",
                          backgroundColor: "#fff9c4",
                        }}
                      >
                        {isImage ? (
                          <CardMedia
                            component="img"
                            image={file.url}
                            sx={{
                              height: 100,
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                            onClick={() => handleExistingFileClick(file)}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 100,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgba(0,0,0,0.05)",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}
                            >
                              PDF File
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleExistingFileClick(file)}
                            >
                              View
                            </Button>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  );
                })}

                {newFiles.map((file, index) => {
                  const isImage =
                    file.type.includes("jpeg") ||
                    file.type.includes("jpg") ||
                    file.type.includes("png");

                  const previewUrl = URL.createObjectURL(file);

                  return (
                    <Grid item xs={6} sm={3} md={2} key={`new-${index}`}>
                      <Card
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          boxShadow: 3,
                          height: 140,
                          border: "1px solid #2e7d32",
                          backgroundColor: "#c8e6c9",
                        }}
                      >
                        {isImage ? (
                          <CardMedia
                            component="img"
                            image={previewUrl}
                            sx={{
                              height: 100,
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                            onClick={() => handleNewFileClick(file)}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 100,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgba(0,0,0,0.05)",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}
                            >
                              PDF File
                            </Typography>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleNewFileClick(file)}
                            >
                              View
                            </Button>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 3 }}
          >
            <Button variant="outlined" onClick={onClose}>
              CLOSE
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={!!previewImage}
        imageUrl={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </>
  );
};

export default FileReadManagerModal;
